import React from "react";
import { useNavigate } from "react-router-dom";
import {
  CloseOutlined,
  DownOutlined
} from "@ant-design/icons";
import { t } from "i18next";
import moment from "moment";
import {
  Row,
  Col,
  Divider,
  Table,
  Breadcrumb,
  Button,
  Input,
  SearchOutlined,
  DatePicker,
  Drawer,
  Form,
  Menu,
  Dropdown,
} from "common/elements";
import { Util } from "common/util";

const util = new Util();
export default function withDataTable({
  title = "",
  subTitle = "",
  pathName = "/",
  backLink = "",
  dateFormat = "",
  pickerDate = "",
  datePlaceholder = "",
  rowClassName = "",
  footerButton = false,
  showDropdownExport = false,
  menuItems = [],
  showFetchListDate = true,
  columns = [],
  componentWillMount = () => { },
  handleSearch = (e) => { },
  filterFields = () => { },
  buttonOption = () => { },
  getCustomColumns = (response) => [],
  getResponseData = (response) => response,
  getData = () => new Promise((resolve) => resolve(null))
}) {
  return () => {
    const [datas, setDatas] = React.useState([]);
    const [customHeadColumns, setCustomHeadColumns] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const navigate = useNavigate();
    const params = new URLSearchParams(document.location.search);
    let timer = null;

    const fetchList = () => {
      setLoading(true);
      getData()
        .then((response) => {
          if (response) {
            setCustomHeadColumns(getCustomColumns(response));
            response = getResponseData(response);
            if (response) {
              setDatas(response);
            }
          }
        })
        .catch(error => {
          if (error.response.status === 403) {
            localStorage.removeItem("ACCESS_TOKEN");
            navigate("/signin");
          }
        })
        .finally(() => setLoading(false));
    };

    const onBack = () => {
      navigate(backLink);
    };

    const onSearch = (e) => {
      clearTimeout(timer);
      handleSearch(e);
      timer = setTimeout(() => {
        fetchList();
      }, 1000);
    };

    const onTableChange = (newPagination, filters, sorter) => {
      fetchList({
        sortField: sorter.field,
        sortOrder: sorter.order,
        pagination: newPagination,
        ...filters,
      });
    };

    React.useMemo(() => {
      componentWillMount();
    }, []);

    React.useEffect(() => {
      fetchList();
      // eslint-disable-next-line
    }, []);

    return (
      <>
        <Row style={styles.breadCream}>
          <Col span={24}>
            <Breadcrumb>
              <Breadcrumb.Item
                onClick={() => navigate("/")}
                style={{ cursor: "pointer" }}
              >
                {t("text_dashboard")}
              </Breadcrumb.Item>
              <Breadcrumb.Item onClick={onBack} style={{ cursor: "pointer" }}>
                {title}
              </Breadcrumb.Item>
              <Breadcrumb.Item style={{ color: "#1d8747" }}>
                {subTitle}
              </Breadcrumb.Item>
            </Breadcrumb>
          </Col>
        </Row>
        <Row style={{ padding: 15 }} id="header-report-table">
          <Col span={12}>
            <Title subTitle={subTitle} />
          </Col>
          <Col
            span={12}
            style={{
              textAlign: "right",
              display: "flex",
              justifyContent: "flex-end",
              paddingRight: 5,
            }}
          >
            <Input
              placeholder={t("text_search_by_employee")}
              defaultValue={params.get("search")}
              prefix={<SearchOutlined />}
              style={styles.generalSearch}
              allowClear={true}
              onChange={onSearch}
            />

            <DatePickerFilter
              fetchList={fetchList}
              pathName={pathName}
              dateFormat={dateFormat}
              datePlaceholder={datePlaceholder}
              pickerDate={pickerDate}
              showFetchListDate={showFetchListDate}
            />
            {React.useMemo(() => (<ExportCostSharingButton enable={showDropdownExport} filtExport={buttonOption} pathName={pathName} menuItems={menuItems} footerButton={footerButton} />), [])}
          </Col>
        </Row>

        <div id="report-table">
          <Table
            bordered
            dataSource={datas}
            pagination={false}
             scroll={
              datas ? {
                y: window.innerHeight - 280,
              } : undefined
            }
            rowClassName={rowClassName}
            onChange={onTableChange}
            rowKey="report-id"
            columns={customHeadColumns.length > 0 ? columns.concat(customHeadColumns) : columns}
            loading={loading}
          />
        </div>
      </>
    );
  };
}

const DatePickerFilter = ({
  pathName,
  fetchList,
  dateFormat,
  datePlaceholder,
  pickerDate,
  showFetchListDate,
}) => {
  const queryParams = new URLSearchParams(document.location.search);
  const onChangeDate = (value, valueString) => {
    const dateString = valueString.split("/");
    if (value) {
      if (dateString.length === 3) {
        queryParams.set("date", util.formatDateForMYSQL(value));
      } else {
        queryParams.set("date", moment(value).format("YYYY-MM"));
      }
    } else {
      queryParams.delete("date");
    }
    util.pushParamsToURL(pathName, queryParams.toString());
    if (showFetchListDate) fetchList();
  };

  return (
    <>
      <Form style={{ marginLeft: "8px" }}>
        <DatePicker
          name="date"
          placeholder={`${datePlaceholder}`}
          format={`${dateFormat}`}
          style={{ marginLeft: "8px", width: "180px" }}
          defaultValue={
            queryParams.get("date") && moment(queryParams.get("date"))
          }
          picker={`${pickerDate}`}
          allowClear={false}
          onChange={onChangeDate}
        />
      </Form>
    </>
  );
};

function Title({ subTitle }) {
  return <span id="title-table-report">{subTitle}</span>;
}

function ExportCostSharingButton({ enable, filtExport, pathName, menuItems, footerButton }) {
  const [visible, setVisible] = React.useState(false);

  const onClose = () => {
    setVisible(false);
  };

  const onShow = (value) => {
    setVisible(true);
    const params = new URLSearchParams(document.location.search);
    if (["first_payment", "second_payment"].includes(value)) {
      params.set("payrollPeriod", value);
      params.delete("type");
    } else {
      params.set("type", value);
      params.delete("payrollPeriod");
    }

    Util.getInstance().pushParamsToURL(pathName, params.toString());
  };

  return (
    enable && (
      <>
        <Dropdown overlay={
          <Menu>
            <Menu.Item onClick={() => onShow("first_payment")}>
              1<sup>st</sup> Payment
            </Menu.Item>
            <Menu.Item onClick={() => onShow("second_payment")}>
              2<sup>nd</sup> Payment
            </Menu.Item>
            <Divider style={{ margin: 0, marginTop: 5, marginBottom: 5, fontSize: 14 }}>agresso</Divider>
            <Menu.Item onClick={() => onShow("ANNUAL_SALARY")}>
              13<sup>th</sup> Month Salary
            </Menu.Item>
            <Menu.Item onClick={() => onShow("SALARY_2ND_PAYMENT")}>
              Salary 2<sup>nd</sup> payment
            </Menu.Item>
            <Menu.Item onClick={() => onShow("PF_NPA")}>
              Provident Fund NPA
            </Menu.Item>
            <Menu.Item onClick={() => onShow("PENSION_FUND_NPA")}>
              Pension Fund NPA Contribution
            </Menu.Item>
            <Menu.Item onClick={() => onShow("PENSION_FUND_NPA_PLUS_TAX")}>
              Pension Fund NPA + Tax Contribution
            </Menu.Item>
            <Menu.Item onClick={() => onShow("PF_STAFF_CONTRIBUTION")}>
             Provident Staff Contribution
            </Menu.Item>
            <Menu.Item onClick={() => onShow("PENSION_FUND_STAFF_CONTRIBUTION")}>
              Pension Fund Staff Contribution
            </Menu.Item>
            <Menu.Item onClick={() => onShow("SENIORITY")}>
              Seniority Identity
            </Menu.Item>
            <Menu.Item onClick={() => onShow("SEVERANCE_PAY")}>
              Severance Pay
            </Menu.Item>
            <Menu.Item onClick={() => onShow("2ND_PAYMENT")}>
              2<sup>nd</sup> Payment
            </Menu.Item>
            <Menu.Item onClick={() => onShow("PHONE_ALLOWANCE")}>
              Phone Allowance
            </Menu.Item>
            <Menu.Item onClick={() => onShow("TAX_ON_SALARY")}>
              Tax on Salary 
            </Menu.Item>
            <Menu.Item onClick={() => onShow("TAX_ON_PHONE_ALLOWANCE")}>
              Tax on Phone Allowance
            </Menu.Item>
            <Menu.Item onClick={() => onShow("TAX_ON_PENSION_FUND_NPA_CONTRIBUTION")}>
              Tax on Pension Fund NPA Contribution
            </Menu.Item>
          </Menu>
        }>
          <Button
            style={{ marginLeft: 10 }}
          >
            {t("text_export")}
            <DownOutlined style={{ fontSize: 14 }} />
          </Button>
        </Dropdown>
        <Drawer
          title={
            <div style={styles.headerFilter}>
              <div>{t("text_export_report")}</div>
              <div>
                <CloseOutlined onClick={onClose} />
              </div>
            </div>
          }
          placement="right"
          closable={false}
          onClose={onClose}
          visible={visible}
          footer={footerButton && <div style={{ visibility: "hidden" }}>ABC</div>}
        >
          {visible ? <ExportExcelFile filtExport={filtExport} /> : null}
        </Drawer>
      </>
    )
  );
}

const ExportExcelFile = ({ filtExport }) => {
  return filtExport();
}

const styles = {
  formInline: {
    width: 203,
    display: "inline-block",
    textAlign: "left",
    marginRight: 5,
    marginBottom: 0,
    marginTop: 0,
  },
  generalSearch: {
    width: 250,
  },
  headerFilter: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  breadCream: {
    paddingBottom: 15,
    backgroundColor: "#EFF3F6",
  },
};
